<!-- src/components/admin/AdminVehicles.vue -->
<template>
  <avs-card>
    <template #title>
      <div class="d-flex flex-column w-100">
        <!-- Titre et stats -->
        <div class="d-flex align-center mb-4">
          <v-icon large class="mr-3">mdi-car-multiple</v-icon>
          <div>
            <h2 class="text-h5 mb-0">Gestion des véhicules</h2>
            <p class="text-caption grey--text mb-0" v-if="totalCount !== null">
              {{ totalCount }} véhicule(s) trouvé(s)
            </p>
          </div>
          <v-spacer />
          <!-- Actions globales -->
          <avs-button
            label
            color="primary"
            class="mr-2"
            iconI="mdi-plus"
            spantext="Nouveau"
            @click="openEditor(null)"
          />
          <avs-button
            label
            color="secondary"
            outlined
            iconI="mdi-download"
            spantext="Exporter"
            :loading="exporting"
            @click="exportVehicles"
          />
        </div>
        <!-- Filtres -->
        <v-toolbar flat class="bgTable rounded" dense>
          <v-row dense align="center">
            <!-- Recherche -->
            <v-col cols="12" md="3">
              <v-text-field
                dense clearable hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Rechercher..."
                v-model="search"
                @input="debouncedSync"
                outlined
                class="rounded"
              />
            </v-col>
            <!-- Société -->
            <v-col cols="12" md="2">
              <v-autocomplete
                v-model="selectedCompany"
                :items="companies"
                item-text="name"
                item-value="pk"
                placeholder="Société"
                dense clearable hide-details outlined
                prepend-inner-icon="mdi-domain"
                @change="onFiltersChange"
              />
            </v-col>
            <!-- Type (multi) -->
            <v-col cols="12" md="2">
              <v-select
                dense clearable hide-details multiple outlined
                :items="typeOptions"
                v-model="typeFilter"
                item-text="text" item-value="value"
                placeholder="Type"
                prepend-inner-icon="mdi-car-side"
                @change="onFiltersChange"
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip v-if="index === 0" x-small>{{ item.text }}</v-chip>
                  <span v-if="index === 1" class="grey--text text-caption ml-1">
                    (+{{ typeFilter.length - 1 }})
                  </span>
                </template>
              </v-select>
            </v-col>
            <!-- Statut -->
            <v-col cols="12" md="2">
              <v-select
                dense clearable hide-details outlined
                :items="statusOptions"
                v-model="statusFilter"
                item-text="text" item-value="value"
                placeholder="Statut"
                prepend-inner-icon="mdi-filter-variant"
                @change="onFiltersChange"
              />
            </v-col>
            <!-- Code secret -->
            <v-col cols="12" md="2">
              <v-text-field
                dense clearable hide-details outlined
                placeholder="Code secret"
                v-model="secret"
                prepend-inner-icon="mdi-lock"
                @input="debouncedSync"
              />
            </v-col>
            <!-- Bouton reset filtres -->
            <v-col cols="auto">
              <avs-button
                icon
                label
                fab
                small
                color="grey"
                iconI="mdi-filter-remove"
                @click="resetFilters"
              />
            </v-col>
          </v-row>
        </v-toolbar>
        <!-- Chips filtres actifs -->
        <div class="d-flex flex-wrap mt-2" v-if="hasActiveFilters">
          <v-chip
            v-if="search"
            small close class="mr-2 mb-1"
            @click:close="search = ''; syncRoute()"
          >
            Recherche: {{ search }}
          </v-chip>
          <v-chip
            v-if="selectedCompany"
            small close class="mr-2 mb-1"
            @click:close="selectedCompany = null; onFiltersChange()"
          >
            Société: {{ companyName }}
          </v-chip>
          <v-chip
            v-for="t in typeFilter"
            :key="t"
            small close class="mr-2 mb-1"
            @click:close="removeTypeFilter(t)"
          >
            Type: {{ t }}
          </v-chip>
          <v-chip
            v-if="statusFilter"
            small close class="mr-2 mb-1"
            @click:close="statusFilter = null; onFiltersChange()"
          >
            Statut: {{ statusLabel(statusFilter) }}
          </v-chip>
        </div>
      </div>
    </template>
    <template #content>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading && page === 1"
        :items-per-page="-1"
        hide-default-footer
        dense
        class="elevation-0 bgTable"
        @dblclick:row="(_, { item }) => openEditor(item)"
      >
        <!-- Société -->
        <template #item.company_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="28" color="primary" class="mr-2">
              <span class="white--text text-caption">
                {{ (item.company_name || '?').charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <span class="text-truncate" style="max-width: 120px;">
              {{ item.company_name || '-' }}
            </span>
          </div>
        </template>
        <!-- Immatriculation + marque/modèle -->
        <template #item.registration_number="{ item }">
          <div>
            <div class="font-weight-bold">{{ item.registration_number }}</div>
            <div class="text-caption grey--text">
              {{ item.make }} {{ item.model }}
            </div>
          </div>
        </template>
        <!-- Chauffeur -->
        <template #item.driver="{ item }">
          <div v-if="item.driver_first_name || item.driver_last_name">
            <div class="font-weight-medium">
              {{ item.driver_first_name }} {{ item.driver_last_name }}
            </div>
            <div class="text-caption grey--text" v-if="item.driver_phone_number">
              <v-icon x-small>mdi-phone</v-icon>
              {{ item.driver_phone_number }}
            </div>
          </div>
          <span v-else class="grey--text">-</span>
        </template>
        <!-- Type -->
        <template #item.vehicle_type="{ item }">
          <v-chip x-small outlined :color="typeColor(item.vehicle_type)">
            {{ item.vehicle_type || '-' }}
          </v-chip>
        </template>
        <!-- Département -->
        <template #item.department_name="{ item }">
          <span>{{ item.department_name || '-' }}</span>
        </template>
        <!-- Produit -->
        <template #item.product_name="{ item }">
          <v-chip v-if="item.product_name" x-small color="info" dark>
            {{ item.product_name }}
          </v-chip>
          <span v-else class="grey--text">-</span>
        </template>
        <!-- Statut -->
        <template #item.status="{ item }">
          <v-chip
            small label
            :color="statusColor(item.status)"
            :dark="item.status !== 'deactivated'"
          >
            <v-icon left x-small>{{ statusIcon(item.status) }}</v-icon>
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <!-- Options (switches) -->
        <template #item.options="{ item }">
          <div class="d-flex align-center" style="gap: 4px;">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <avs-button
                    icon
                    label
                    fab
                    small
                    :color="item.is_kmh ? 'primary' : 'grey'"
                    iconI="mdi-speedometer"
                    @click.stop="toggleOption(item, 'is_kmh')"
                  />
                </div>
              </template>
              <span>Kilométrage {{ item.is_kmh ? 'obligatoire' : 'non obligatoire' }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <avs-button
                    icon
                    label
                    fab
                    small
                    :color="item.can_take_picture ? 'primary' : 'grey'"
                    iconI="mdi-camera"
                    @click.stop="toggleOption(item, 'can_take_picture')"
                  />
                </div>
              </template>
              <span>Photo {{ item.can_take_picture ? 'obligatoire' : 'non obligatoire' }}</span>
            </v-tooltip>
          </div>
        </template>
        <!-- Toggles activation/blocage -->
        <template #item.toggles="{ item }">
          <div class="d-flex align-center">
            <v-switch
              class="mt-0 mr-2" dense hide-details
              v-model="item.is_activated"
              color="success"
              @change="toggleActivated(item)"
            />
            <v-switch
              class="mt-0" dense hide-details
              v-model="item.is_blocked"
              color="error"
              @change="toggleBlocked(item)"
            />
          </div>
        </template>
        <!-- Date création -->
        <template #item.created_at="{ item }">
          <span class="text-caption">{{ toDatetime(item.created_at) }}</span>
        </template>
        <!-- Actions -->
        <template #item.actions="{ item }">
          <div class="d-flex justify-center" style="gap: 4px;">
            <!-- Modifier -->
            <avs-button
              icon
              fab
              label
              small
              color="primary"
              iconI="mdi-pencil"
              @click.stop="openEditor(item)"
            />
            <!-- Télécharger badge -->
            <template v-if="downloadingId === item.id">
              <v-progress-circular indeterminate size="24" width="2" />
            </template>
            <template v-else>
              <avs-button
                icon
                label
                fab
                small
                color="secondary"
                iconI="mdi-download"
                @click.stop="downloadBadge(item)"
              />
            </template>
            <!-- Voir les bons -->
            <avs-button
              icon
              label
              fab
              small
              color="info"
              iconI="mdi-ticket"
              @click.stop="viewVouchers(item)"
            />
          </div>
        </template>
      </v-data-table>
      <!-- Loader bas -->
      <div class="py-4 d-flex justify-center" v-if="loadingMore">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <!-- Fin de liste -->
      <div
        v-if="!loading && !loadingMore && !hasMore && items.length"
        class="text-center grey--text text--darken-1 py-4"
      >
        <v-icon small class="mr-1">mdi-check-circle</v-icon>
        Fin de la liste ({{ items.length }} véhicule(s))
      </div>
      <!-- Liste vide -->
      <div
        v-if="!loading && !items.length"
        class="text-center py-12"
      >
        <v-icon size="64" color="grey lighten-1">mdi-car-off</v-icon>
        <p class="grey--text mt-4">Aucun véhicule trouvé</p>
        <avs-button
          color="primary"
          label
          small
          iconI="mdi-filter-remove"
          spantext="Réinitialiser les filtres"
          @click="resetFilters"
        />
      </div>
      <!-- Sentinelle infinite scroll -->
      <div ref="sentinel" style="height: 1px;"></div>
      <!-- Dialog édition -->
      <v-dialog v-model="dialogOpen" max-width="800" persistent>
        <AdminVehicleEditor
          v-if="dialogOpen"
          :value="selected"
          @cancel="dialogOpen = false"
          @saved="onSaved"
        />
      </v-dialog>
      <!-- Dialog bons du véhicule -->
      <v-dialog v-model="dialogVouchers" max-width="600">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-ticket</v-icon>
            Bons de {{ selectedVehicle ? selectedVehicle.registration_number : '' }}
            <v-spacer />
            <avs-button
              icon
              label
              fab
              small
              color="grey"
              iconI="mdi-close"
              @click="dialogVouchers = false"
            />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="voucherHeaders"
              :items="vehicleVouchers"
              :loading="loadingVouchers"
              dense
              hide-default-footer
            >
              <template #item.status="{ item }">
                <v-chip x-small :color="item.is_all_used ? 'grey' : 'success'" dark>
                  {{ item.is_all_used ? 'Épuisé' : 'Actif' }}
                </v-chip>
              </template>
              <template #item.remaining="{ item }">
                {{ item.liter_remaining }} L / {{ item.remaining_amount }} XOF
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <avs-button
              label="Fermer"
              color="primary"
              spantext="Fermer"
              @click="dialogVouchers = false"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import AdminServices from "@/services/AdminServices"
import { CompanyService, CompanyVehiclesService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import AdminVehicleEditor from "./AdminVehicleEditor.vue"
import debounce from "lodash/debounce"

const TYPES = ['Motorcycle', 'Truck', 'Tricycle', 'Sedan', 'SUV', 'PICK-UP', 'OTHER', 'VL']

export default {
  name: "AdminVehicles",
  components: { AdminVehicleEditor },
  data() {
    return {
      // Filtres
      search: "",
      typeFilter: [],
      statusFilter: null,
      selectedCompany: null,
      companies: [],
      secret: "",
      downloadingId: null,
      exporting: false,
      // Liste
      loading: false,
      loadingMore: false,
      items: [],
      page: 1,
      hasMore: true,
      totalCount: null,
      observer: null,
      // Dialog édition
      dialogOpen: false,
      selected: null,
      // Dialog bons
      dialogVouchers: false,
      selectedVehicle: null,
      vehicleVouchers: [],
      loadingVouchers: false,
      headers: [
        { text: this.$t("Société"), value: "company_name", width: 150 },
        { text: this.$t("Immatriculation"), value: "registration_number", width: 140 },
        { text: this.$t("Type"), value: "vehicle_type", width: 100 },
        { text: this.$t("Chauffeur"), value: "driver", width: 150 },
        { text: this.$t("Département"), value: "department_name", width: 120 },
        { text: this.$t("Produit"), value: "product_name", width: 100 },
        { text: this.$t("Statut"), value: "status", width: 110 },
        { text: this.$t("Options"), value: "options", sortable: false, width: 80 },
        { text: this.$t("Act/Bloq"), value: "toggles", sortable: false, width: 120 },
        { text: this.$t("Créé le"), value: "created_at", width: 100 },
        { text: "", value: "actions", sortable: false, align: "center", width: 120 },
      ],
      voucherHeaders: [
        { text: "ID", value: "id", width: 80 },
        { text: "Litres", value: "purchase_liters" },
        { text: "Montant", value: "purchase_amount" },
        { text: "Restant", value: "remaining" },
        { text: "Statut", value: "status" },
      ],
      typeOptions: TYPES.map(v => ({ text: v, value: v })),
      statusOptions: [
        { text: this.$t("Tous"), value: null },
        { text: this.$t("Actif"), value: "active" },
        { text: this.$t("Bloqué"), value: "blocked" },
        { text: this.$t("Désactivé"), value: "deactivated" },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      return this.search || this.selectedCompany || this.typeFilter.length || this.statusFilter || this.secret
    },
    companyName() {
      const c = this.companies.find(c => c.pk === this.selectedCompany)
      return c?.name || ''
    },
  },
  created() {
    this.debouncedSync = debounce(this.syncRoute, 300)
    this.fetchCompanies()
  },
  mounted() {
    this.applyRouteFilters()
    this.fetchReset()
    this.$nextTick(this.createObserver)
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteFilters()
        this.fetchReset()
      },
      deep: false,
    },
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },
  methods: {
    toDatetime,
    
    fetchCompanies() {
      CompanyService.getAllCompanies().then(r => { this.companies = r || [] })
    },
    
    // Couleurs et labels
    statusColor(s) {
      return { active: 'success', blocked: 'error', deactivated: 'grey' }[s] || 'grey'
    },
    statusIcon(s) {
      return { active: 'mdi-check-circle', blocked: 'mdi-block-helper', deactivated: 'mdi-power-off' }[s] || 'mdi-help-circle'
    },
    statusLabel(s) {
      return {
        active: this.$t('Actif'),
        blocked: this.$t('Bloqué'),
        deactivated: this.$t('Désactivé'),
      }[s] || s
    },
    typeColor(t) {
      const colors = {
        Motorcycle: 'orange',
        Truck: 'brown',
        Tricycle: 'teal',
        Sedan: 'blue',
        SUV: 'indigo',
        'PICK-UP': 'deep-purple',
        VL: 'cyan',
        OTHER: 'grey',
      }
      return colors[t] || 'grey'
    },
    
    // Filtres
    applyRouteFilters() {
      const q = this.$route?.query || {}
      this.search = q.search || ""
      if (q.type) {
        const values = String(q.type).split(',').map(s => s.trim())
        this.typeFilter = values.filter(v => TYPES.includes(v))
      } else {
        this.typeFilter = []
      }
      this.statusFilter = q.status || null
      this.secret = q.secret || ""
      this.selectedCompany = q.company ? Number(q.company) : null
    },
    syncRoute() {
      const q = {
        ...(this.search ? { search: this.search } : {}),
        ...(this.typeFilter.length ? { type: this.typeFilter.join(',') } : {}),
        ...(this.statusFilter ? { status: this.statusFilter } : {}),
        ...(this.secret ? { secret: this.secret } : {}),
        ...(this.selectedCompany ? { company: String(this.selectedCompany) } : {}),
      }
      this.$router.replace({ name: 'AdminVehicles', query: q }).catch(() => {})
    },
    onFiltersChange() {
      this.syncRoute()
    },
    resetFilters() {
      this.search = ""
      this.typeFilter = []
      this.statusFilter = null
      this.secret = ""
      this.selectedCompany = null
      this.syncRoute()
    },
    removeTypeFilter(t) {
      this.typeFilter = this.typeFilter.filter(x => x !== t)
      this.onFiltersChange()
    },
    
    // Params backend
    params() {
      const p = { page: this.page, ordering: "-created_at" }
      if (this.search) p.search = this.search
      if (this.typeFilter.length) p.type = this.typeFilter.join(',')
      if (this.statusFilter) p.status = this.statusFilter
      if (this.secret) p.secret = this.secret
      if (this.selectedCompany) p.company = this.selectedCompany
      return p
    },
    
    // Fetch
    async fetch({ reset = false } = {}) {
      if (reset) this.loading = true
      else this.loadingMore = true
      try {
        const data = await AdminServices.getVehicles(this, this.params())
        const results = Array.isArray(data) ? data : (data.results || [])
        results.forEach(r => (r.make_model = [r.make, r.model].filter(Boolean).join(" ")))
        this.items = reset ? results : this.items.concat(results)
        this.hasMore = !!(data && data.next)
        if (data && data.count !== undefined) this.totalCount = data.count
      } catch (e) {
        if (e?.response?.status === 404) this.hasMore = false
        else console.error("Erreur chargement véhicules:", e)
      } finally {
        this.loading = false
        this.loadingMore = false
        this.$nextTick(() => {
          if (this.hasMore && this.observer && this.$refs.sentinel) {
            this.observer.unobserve(this.$refs.sentinel)
            this.observer.observe(this.$refs.sentinel)
          }
        })
      }
    },
    fetchReset() {
      this.page = 1
      this.hasMore = true
      this.items = []
      this.totalCount = null
      this.fetch({ reset: true })
    },
    
    // Infinite scroll
    createObserver() {
      const el = this.$refs.sentinel
      if (!el) return
      this.observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) this.loadMore() },
        { root: null, rootMargin: "0px", threshold: 0.1 }
      )
      this.observer.observe(el)
    },
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return
      this.page += 1
      this.fetch()
    },
    
    // Actions
    openEditor(item = null) {
      this.selected = item ? JSON.parse(JSON.stringify(item)) : null
      this.dialogOpen = true
    },
    onSaved() {
      this.dialogOpen = false
      this.fetchReset()
      this.$store.commit("app/message", { msg: this.$t("messages.success.operation"), color: "success" })
    },
    toggleActivated(item) {
      const prev = !item.is_activated
      AdminServices.updateVehicleStatus(this, item.id, { is_activated: !!item.is_activated })
        .then(() => this.$store.commit("app/message", { msg: "Statut mis à jour", color: "success" }))
        .catch(() => { item.is_activated = prev })
    },
    toggleBlocked(item) {
      const prev = !item.is_blocked
      AdminServices.updateVehicleStatus(this, item.id, { is_blocked: !!item.is_blocked })
        .then(() => this.$store.commit("app/message", { msg: "Statut mis à jour", color: "success" }))
        .catch(() => { item.is_blocked = prev })
    },
    toggleOption(item, field) {
      const newValue = !item[field]
      AdminServices.updateVehicle(this, item.id, { [field]: newValue })
        .then(() => {
          item[field] = newValue
          this.$store.commit("app/message", { msg: "Option mise à jour", color: "success" })
        })
        .catch(() => {
          this.$store.commit("app/message", { msg: "Erreur", color: "error" })
        })
    },
    downloadBadge(item) {
      this.downloadingId = item.id
      CompanyVehiclesService.getBadge(this, item.id)
        .then(blobUrl => {
          const link = document.createElement("a")
          link.href = blobUrl
          link.download = `badge_${item.registration_number}.png`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000)
        })
        .catch(() => {
          this.$store.commit("app/message", {
            msg: "Erreur téléchargement badge",
            color: "error",
          })
        })
        .finally(() => {
          this.downloadingId = null
        })
    },
    viewVouchers(item) {
      this.selectedVehicle = item
      this.dialogVouchers = true
      this.loadingVouchers = true
      this.vehicleVouchers = []
      
      AdminServices.getVouchers(this, { vehicle: item.id })
        .then(data => {
          this.vehicleVouchers = data.results || data || []
        })
        .finally(() => {
          this.loadingVouchers = false
        })
    },
    async exportVehicles() {
      this.exporting = true
      try {
        const params = this.params()
        delete params.page
        params.format = 'xlsx'
        
        const response = await this.$http.get("/account/admin/vehicles/export", {
          params,
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `vehicules_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        this.$store.commit("app/message", { msg: "Erreur export", color: "error" })
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>